import type * as PAPICore from "@ascent/papi-core";
import { sendPingback } from "@ascent/papi-core/client";
import type * as Server from "@rampjs/server";

type ApiErrorBody = {
  error?: { code?: number | string; message?: string; request_id?: string };
  search?: { response?: ApiErrorBody };
};

function buildErrorPingbackUrl(
  serverUrl: string,
  reason: string,
  extras: Record<string, string | undefined>,
): URL | null {
  try {
    const url = new URL("/pingback", serverUrl);
    url.searchParams.set("event", "client_error");
    url.searchParams.set("reason", reason);
    url.searchParams.set("page_url", window.location.href);
    url.searchParams.set("page_referrer", document.referrer);
    url.searchParams.set("domain", window.location.hostname);
    for (const [key, value] of Object.entries(extras)) {
      if (value !== undefined && value !== "") {
        url.searchParams.set(key, value);
      }
    }
    return url;
  } catch {
    return null;
  }
}

function sendErrorPingback(
  serverUrl: string,
  reason: string,
  extras: Record<string, string | undefined> = {},
): void {
  const url = buildErrorPingbackUrl(serverUrl, reason, extras);
  if (url) sendPingback(url);
}

function extractApiError(
  body: ApiErrorBody | undefined,
): ApiErrorBody["error"] | undefined {
  return body?.error ?? body?.search?.response?.error;
}

/**
 * The canonical shape every data source resolves to. Downstream code (module
 * registry, injectable-units, debug overlay) reads only `server` — it must NEVER
 * branch on where the data came from. `requestPayload` / `rampJsServerUrl` are
 * present only on the fetched path (the inlined path issues no request).
 */
export type ResponseResult = {
  server: Server.Response;
  requestPayload?: Server.Body;
  rampJsServerUrl?: string;
};

/**
 * Normalize an inlined PAPI `RequestResponse` (as a host page exposes it on
 * `window.__RAMPJS__.response`) into the `@rampjs/server` envelope the client
 * consumes everywhere else. The renderer reads `response.search`; the client
 * reads `server.search.response`, so we wrap the processed/raw responses one
 * level deeper and pass the remaining metadata through unchanged.
 */
function normalizeInlinedResponse(
  response: PAPICore.RequestResponse,
): Server.Response {
  return {
    search: { response: response.search },
    rawSearch: response.rawSearch
      ? { response: response.rawSearch }
      : undefined,
    changes: response.changes,
    papiCoreUrl: response.papiCoreUrl,
    _debug: response._debug,
  };
}

/**
 * Resolve the PAPI response via capability detection — NOT a mode flag. If a
 * host page has inlined the data on `window.__RAMPJS__.response`, consume it
 * (no network request). Otherwise fall through to a server `fetch`. The same
 * bundle therefore works on SSR pages, publisher-injection pages, and any
 * future inlined-data surface, and downstream code sees one canonical shape.
 */
export async function getResponse(): Promise<ResponseResult> {
  const inlined = window.__RAMPJS__?.response;
  if (inlined) {
    return { server: normalizeInlinedResponse(inlined) };
  }
  return request();
}

/**
 * Sends a request to the server and returns a response.
 *
 * @returns A promise that resolves to a `Server.Response` object.
 */
export async function request(): Promise<{
  server: Server.Response;
  requestPayload: Server.Body;
  rampJsServerUrl: string;
}> {
  const RAMPJS = window.RAMPJS ?? {};

  const body: Server.Body = {
    pageReferrer: document.referrer,
    pageUrl: window.location.href,
    theme: RAMPJS.theme,
    // Opaque pass-through: forward the partner's config object untouched when
    // present. Omitted from the body entirely when unset so the common path is
    // unchanged. See `window.RAMPJS.directParams` (ABE-795).
    directParams: RAMPJS.directParams,
  };

  let response: globalThis.Response;
  try {
    response = await fetch(RAMPJS_SERVER_URL, {
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
  } catch (error) {
    sendErrorPingback(RAMPJS_SERVER_URL, "fetch_failed", {
      error_message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }

  if (!response.ok) {
    let apiError: ApiErrorBody["error"] | undefined;
    try {
      apiError = extractApiError(
        (await response.clone().json()) as ApiErrorBody,
      );
    } catch {
      // body wasn't JSON — fall through to plain http_${status}
    }
    if (apiError?.code !== undefined) {
      sendErrorPingback(RAMPJS_SERVER_URL, `api_error_${apiError.code}`, {
        request_id: apiError.request_id,
        error_code: String(apiError.code),
        error_message: apiError.message,
        http_status: String(response.status),
      });
    } else {
      sendErrorPingback(RAMPJS_SERVER_URL, `http_${response.status}`, {
        error_message: apiError?.message,
      });
    }
    if (__DEBUG__) {
      throw new Error(
        `RAMP.js Server request failed with status ${response.status}`,
      );
    } else {
      throw new Error("[RampJs] Server request failed.");
    }
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (error) {
    sendErrorPingback(RAMPJS_SERVER_URL, "parse_failed", {
      http_status: String(response.status),
      error_message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
  const server = json as Server.Response;

  const apiError = extractApiError(json as ApiErrorBody);
  if (apiError) {
    sendErrorPingback(
      RAMPJS_SERVER_URL,
      `api_error_${apiError.code ?? "unknown"}`,
      {
        request_id: apiError.request_id,
        error_code:
          apiError.code !== undefined ? String(apiError.code) : undefined,
        error_message: apiError.message,
      },
    );
  } else if (!server?.search?.response) {
    sendErrorPingback(RAMPJS_SERVER_URL, "empty_response", {});
  }

  const detail = structuredClone(server);
  const event = new CustomEvent("rampjs:server", { detail });
  window.dispatchEvent(event);

  return { server, requestPayload: body, rampJsServerUrl: RAMPJS_SERVER_URL };
}
